import { LocalizedString } from "../../services/redux/types/CartLoyaltyV2Types";
import { LoyaltyOfferType } from "../loyaltyTypes";

/**
 * The promo-material contract (story #4087) as this app reads it.
 *
 * The server decides *which* pop-up belongs to each moment of the journey and
 * answers with a **directive**: a `type` naming the component to render and the
 * subject to render it with. None of the ordering rules — the 2-hour session,
 * the once-ever campaign rule, the download-app cooldown — live here.
 *
 * Contract: loyalty-service/docs/promo-material-api-contract.md
 */

/** Where the customer is. The server answers a different ladder per stage. */
export enum PopupStage {
  HOME = "HOME",
  SIGN_IN = "SIGN_IN",
  CHECKOUT = "CHECKOUT",
  ORDER_CONFIRMATION = "ORDER_CONFIRMATION",
}

/**
 * Which component to render. This lists what *this build* knows how to show —
 * the wire is not promised to stay inside it. Anything outside falls to the
 * resolver's `default`, which degrades to "one fewer pop-up" rather than a
 * crash, so a server that learns a new type never breaks a deployed client.
 */
export enum PromoPopupType {
  /** One offer to push — the single-reward pop-up, campaign-dressed or not. */
  REWARD = "REWARD",
  /** Every applicable offer, when there is more than one. */
  ALL_REWARDS = "ALL_REWARDS",
  /** The offer the customer arrived for no longer applies to them. */
  OFFER_UNAVAILABLE = "OFFER_UNAVAILABLE",
  /** Web only, gated by core's per-restaurant flag and cooldown. */
  DOWNLOAD_APP = "DOWNLOAD_APP",
  /** An announcement campaign's own pixels. */
  ANNOUNCEMENT = "ANNOUNCEMENT",
  /** The customer moved tier (up, down, or held steady). */
  TIER_MOVEMENT = "TIER_MOVEMENT",
}

export enum PromoContentType {
  IMAGE = "IMAGE",
  TEXT = "TEXT",
}

export enum PromoActionType {
  NONE = "NONE",
  APPLY_OFFER = "APPLY_OFFER",
  REDIRECT = "REDIRECT",
}

/** Where a REDIRECT action goes. Menu ids arrive already translated to core's. */
export enum PromoRedirectPage {
  HOME = "HOME",
  MENU = "MENU",
  MENU_CATEGORY = "MENU_CATEGORY",
  ITEM = "ITEM",
  REWARDS = "REWARDS",
  WALLET = "WALLET",
  PREVIOUS_ORDERS = "PREVIOUS_ORDERS",
}

export type PromoImageContent = {
  type: PromoContentType.IMAGE;
  imageUrl: string;
};

export type PromoTextContent = {
  type: PromoContentType.TEXT;
  headline: LocalizedString;
  description?: LocalizedString;
  buttonText?: LocalizedString;
};

export type PromoContent = PromoImageContent | PromoTextContent;

export type PromoAction = {
  type: PromoActionType;
  /** APPLY_OFFER: the loyalty offer uuid to apply. */
  offerId?: string;
  /** REDIRECT only, and only for the destinations that need a target. */
  page?: PromoRedirectPage;
  categoryId?: number;
  itemId?: number;
};

type PromoMovementTier = {
  id: string;
  name: LocalizedString;
  color?: string;
};

/** Which way the customer moved. Same open-wire caveat as PromoPopupType. */
export enum PromoTierDirection {
  UPGRADE = "UPGRADE",
  DOWNGRADE = "DOWNGRADE",
  SAME_TIER = "SAME_TIER",
}

type PromoTierMovement = {
  /** Echoed back on display — that echo is what retires the movement. */
  tierHistoryId: number;
  direction: PromoTierDirection;
  fromTier: PromoMovementTier;
  toTier: PromoMovementTier;
};

/** One pop-up the server chose, in the order it should be shown. */
export type PromoPopupDirective = {
  type: PromoPopupType;
  /** Set iff the admin authored it. Echoed back on display (once-ever keying). */
  materialId?: string;
  /** REWARD: the offer to push. OFFER_UNAVAILABLE: the one that stopped applying. */
  offerId?: string;
  /** ALL_REWARDS: exactly the offers this pop-up covers. */
  offerIds?: string[];
  content?: PromoContent;
  action?: PromoAction;
  /**
   * DOWNLOAD_APP only, and the one offer sent inline rather than by uuid: it is
   * redeemable in the app, so it is absent from this channel's offer list and
   * there is nothing to resolve it against.
   */
  offer?: LoyaltyOfferType;
  tierMovement?: PromoTierMovement;
};

export type GetPromoPopupsResponse = { popups: PromoPopupDirective[] };

/** One banner of the home rail, in the server's display order. */
export type PromoBannerItem = {
  id: string;
  content: PromoContent;
  action: PromoAction;
};

export type GetPromoBannersResponse = { banners: PromoBannerItem[] };

/**
 * What the client reports when a pop-up becomes visible — only the *durable*
 * facts. The session-throttled families ride the `shown` hints instead, so a
 * report of one is knowingly dropped server-side and never sent from here.
 */
export type PromoPopupDisplayBody = {
  type: PromoPopupType;
  stage: PopupStage;
  /** Admin-authored pop-ups: keys the once-ever rule. */
  materialId?: string;
  offerId?: string;
  /** TIER_MOVEMENT: retires the movement. */
  tierHistoryId?: number;
};
